import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { Tooltip, TooltipProps } from 'antd';

export interface TruncatedTooltipProps
  extends Omit<TooltipProps, 'title' | 'visible' | 'onVisibleChange'> {
  /** Il contenuto di testo o nodo da visualizzare/troncare */
  children: ReactNode | string;
  /** Classi da applicare al contenitore del testo */
  className?: string;
  /** Stili da applicare al contenitore del testo */
  style?: React.CSSProperties;
}

const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
  children,
  className,
  style,
  overlayStyle,
  ...tooltipProps
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [visible, setVisible] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Controlla se il contenuto è troncato
  useEffect(() => {
    const checkTruncation = () => {
      const el = textRef.current;
      if (el) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [children]);

  // Gestisce l'apertura/chiusura del Tooltip
  const handleVisibleChange = (nextVisible: boolean) => {
    if (nextVisible && isTruncated) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  };

  return (
    <Tooltip
      title={children}
      open={visible}
      onOpenChange={handleVisibleChange}
      {...tooltipProps}
    >
      <div
        ref={textRef}
        className={className}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...style
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

export default TruncatedTooltip;
